// components/VersionChecker.js
// Polls /api/version every 60 seconds.
// When the server version changes (new deployment), shows a toast banner
// and does a hard reload after 3 seconds.
//
// Usage — add <VersionChecker /> once in App.js:
//   import VersionChecker from './components/VersionChecker';
//   ...
//   return (
//     <>
//       <VersionChecker />
//       {/* rest of app */}
//     </>
//   );

import React, { Component } from "react";

const POLL_INTERVAL_MS = 60000; // check every 60 s
const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

class VersionChecker extends Component {
  constructor(props) {
    super(props);
    this.state = {
      currentVersion: null, // version loaded when the tab opened
      updateAvailable: false,
      countdown: 5,
    };
    this._pollTimer = null;
    this._countdownTimer = null;
  }

  async componentDidMount() {
    // Capture the version the user is currently on
    const initial = await this.fetchVersion();
    if (initial) this.setState({ currentVersion: initial });

    // Start polling
    this._pollTimer = setInterval(
      () => this.checkForUpdate(),
      POLL_INTERVAL_MS,
    );
  }

  componentWillUnmount() {
    clearInterval(this._pollTimer);
    clearInterval(this._countdownTimer);
  }

  async fetchVersion() {
    try {
      const res = await fetch(`${API_BASE}/version`);
      const data = await res.json();
      return data.version || null;
    } catch {
      return null; // network error — ignore silently
    }
  }

  async checkForUpdate() {
    const { currentVersion, updateAvailable } = this.state;
    if (updateAvailable || !currentVersion) return;

    const latest = await this.fetchVersion();
    if (latest && latest !== currentVersion) {
      console.log(
        `[VersionChecker] New version detected: ${latest} (was ${currentVersion})`,
      );
      this.setState({ updateAvailable: true, countdown: 5 });
      this.startCountdown();
    }
  }

  startCountdown() {
    this._countdownTimer = setInterval(() => {
      this.setState((prev) => {
        if (prev.countdown <= 1) {
          clearInterval(this._countdownTimer);
          // Hard reload — bypasses browser cache
          window.location.reload(true);
          return { countdown: 0 };
        }
        return { countdown: prev.countdown - 1 };
      });
    }, 1000);
  }

  handleReloadNow() {
    clearInterval(this._countdownTimer);
    window.location.reload(true);
  }

  handleDismiss() {
    clearInterval(this._countdownTimer);
    // Just dismiss — don't reload. Next poll may show it again.
    this.setState({ updateAvailable: false });
  }

  render() {
    const { updateAvailable, countdown } = this.state;
    if (!updateAvailable) return null;

    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
          background: "linear-gradient(135deg, #1E2D4F 0%, #2E75B6 100%)",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 24px",
          boxShadow: "0 3px 16px rgba(0,0,0,0.30)",
          fontFamily: "Sora, sans-serif",
          fontSize: 13,
          gap: 16,
        }}
      >
        {/* Icon + message */}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 18 }}>🚀</span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 13 }}>
              New update available!
            </div>
            <div style={{ fontSize: 11, color: "#BFD7EF", marginTop: 2 }}>
              KTS Booking has been updated. Reloading in{" "}
              <strong style={{ color: "#FCD34D" }}>{countdown}s</strong> to
              apply latest changes.
            </div>
          </div>
        </div>

        {/* Countdown ring */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexShrink: 0,
          }}
        >
          {/* Progress bar */}
          <div
            style={{
              width: 80,
              height: 4,
              background: "rgba(255,255,255,0.2)",
              borderRadius: 2,
            }}
          >
            <div
              style={{
                width: `${(countdown / 5) * 100}%`,
                height: "100%",
                background: "#FCD34D",
                borderRadius: 2,
                transition: "width 0.9s linear",
              }}
            />
          </div>

          {/* Reload now button */}
          <button
            onClick={() => this.handleReloadNow()}
            style={{
              padding: "6px 14px",
              borderRadius: 8,
              border: "none",
              background: "#FCD34D",
              color: "#1E2D4F",
              fontSize: 12,
              fontWeight: 700,
              cursor: "pointer",
              fontFamily: "Sora, sans-serif",
              whiteSpace: "nowrap",
            }}
          >
            Reload Now
          </button>

          {/* Dismiss */}
          <button
            onClick={() => this.handleDismiss()}
            style={{
              padding: "6px 10px",
              borderRadius: 8,
              border: "1px solid rgba(255,255,255,0.25)",
              background: "transparent",
              color: "#BFD7EF",
              fontSize: 12,
              cursor: "pointer",
              fontFamily: "Sora, sans-serif",
            }}
          >
            Later
          </button>
        </div>
      </div>
    );
  }
}

export default VersionChecker;
